import { useCallback, useEffect, useRef, useState } from 'react';
import type { useQueryClient } from '@tanstack/react-query';
import {
  getProcess,
  type ProcessDetailDto,
  type ProcessGraphDto,
} from '@api/processes';
import { processDetailQueryKey } from '../hooks/useProcessDetail';

/**
 * What one sync attempt did.
 *
 * - `applied` — the server had a newer version and it is now on the canvas.
 * - `unchanged` — nothing to do; the server is where we already were.
 * - `deferred-dirty` — the canvas has unsaved edits, so the reseed would
 *   have discarded them. Owed and retried, never dropped.
 * - `failed` — the refetch itself failed. Owed and retried.
 */
export type SyncOutcome = 'applied' | 'unchanged' | 'deferred-dirty' | 'failed';

/**
 * How many times one sync sequence will re-fetch to answer requests that
 * arrived mid-flight before it gives up and leaves the rest owed.
 */
const MAX_COALESCED_PASSES = 3;

export interface ProcessGraphSyncArgs {
  processId: string | undefined;
  queryClient: ReturnType<typeof useQueryClient>;
  /** Unsaved local edits on the canvas. */
  isDirty: boolean;
  setGraph: (graph: ProcessGraphDto) => void;
  /** {@code useProcessAutoSave().adoptExternalSave}. */
  adoptExternalSave: () => void;
  /** {@code useProcessAutoSave().cancelExternalSaveAdoption}. */
  cancelExternalSaveAdoption: () => void;
}

export interface ProcessGraphSync {
  /** Identity-stable, so callers can depend on it without re-firing. */
  syncNow: () => Promise<SyncOutcome>;
  /**
   * Take the server's version even though the canvas is dirty, discarding
   * the local edits. Backs the "Changes available → Refresh" action.
   */
  discardLocalEditsAndSync: () => Promise<SyncOutcome>;
  isSyncing: boolean;
  /**
   * The server holds a version we could not apply because the canvas is
   * dirty. Drives the "changes available" affordance; cleared as soon as a
   * sync lands or finds nothing new.
   */
  deferredChangesWaiting: boolean;
}

/**
 * Converge the canvas onto the server's copy of the process.
 *
 * The assistant edits on the user's behalf, in this tab, so its save is
 * adopted as the autosave baseline rather than treated as a competing
 * writer — otherwise the reseed below looks like a local edit and the next
 * PUT 409s with a "changed elsewhere" modal the user cannot explain. The
 * adoption has to be armed before the refetch that carries the new version,
 * so a refetch that brings nothing new has to disarm it again.
 *
 * Unlike the one-shot refresh this replaces (AIB-8117), a sync that cannot
 * run is *owed* rather than dropped: {@code pendingRef} holds it and the
 * dirty-to-clean transition drains it. Callers drain it too, by calling
 * {@link ProcessGraphSync.syncNow} again on tab return or at the next turn
 * boundary. Without that, one missed attempt left the canvas stale until a
 * page reload.
 *
 * Deliberately independent of the autosave drift machinery: this never arms
 * {@code confirmDriftRecovery}, because arming it outside a real 409 leaks
 * forward and silently dismisses the next genuine drift modal.
 */
export const useProcessGraphSync = ({
  processId,
  queryClient,
  isDirty,
  setGraph,
  adoptExternalSave,
  cancelExternalSaveAdoption,
}: ProcessGraphSyncArgs): ProcessGraphSync => {
  const [isSyncing, setIsSyncing] = useState(false);
  const [deferredChangesWaiting, setDeferredChangesWaiting] = useState(false);

  // A sync we owe. Set whenever an attempt could not complete; cleared when
  // an attempt reaches the server.
  const pendingRef = useRef(false);
  // The attempt currently talking to the server. Requests coalesce into it,
  // and the user's explicit Refresh queues behind it — two attempts running
  // together would both arm the single adoption flag and then cancel each
  // other's, so the survivor would reseed without rebasing autosave and the
  // user's next edit would 409 for no visible reason.
  const inFlightRef = useRef<Promise<SyncOutcome> | null>(null);
  // Counts sync requests, so an attempt can tell whether a newer one arrived
  // while it was waiting. Its own refetch may have been issued before the
  // write that newer request is about, so its result cannot answer it.
  const requestSeqRef = useRef(0);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Read through refs so the returned callbacks keep a stable identity: they
  // are dependencies of the caller's turn-boundary and visibility effects,
  // and a new function every render would re-fire those.
  const isDirtyRef = useRef(isDirty);
  const processIdRef = useRef(processId);
  const setGraphRef = useRef(setGraph);
  const adoptRef = useRef(adoptExternalSave);
  const cancelAdoptRef = useRef(cancelExternalSaveAdoption);
  const queryClientRef = useRef(queryClient);
  useEffect(() => {
    isDirtyRef.current = isDirty;
    processIdRef.current = processId;
    setGraphRef.current = setGraph;
    adoptRef.current = adoptExternalSave;
    cancelAdoptRef.current = cancelExternalSaveAdoption;
    queryClientRef.current = queryClient;
  });

  const setWaiting = useCallback((waiting: boolean) => {
    if (mountedRef.current) setDeferredChangesWaiting(waiting);
  }, []);

  /**
   * Learn whether the server moved without touching the shared cache.
   *
   * Invalidating while the dirty guard holds would land a new payload in the
   * cache while {@code useProcessGraph}'s seed reference stayed behind it,
   * and its reseed would then fire on the next clean render against a graph
   * the user never asked for.
   */
  const probeForNewVersion = useCallback(
    async (id: string) => {
      try {
        const server = await getProcess(id);
        const cached = queryClientRef.current.getQueryData<ProcessDetailDto>(
          processDetailQueryKey(id),
        );
        setWaiting(server.updatedAt !== cached?.updatedAt);
      } catch {
        // Leave the flag as it stands — the owed retry will settle it.
      }
    },
    [setWaiting],
  );

  /**
   * One refetch-and-reseed round trip. Assumes the dirty guard has already
   * been resolved and that no other attempt is running.
   */
  const runAttempt = useCallback(
    async (id: string, discardLocalEdits: boolean): Promise<SyncOutcome> => {
      const client = queryClientRef.current;
      const queryKey = processDetailQueryKey(id);
      const staleUpdatedAt =
        client.getQueryData<ProcessDetailDto>(queryKey)?.updatedAt;

      // Cleared up front, not on success: a request that arrives while this
      // attempt is in flight then leaves the flag set, because this attempt's
      // refetch may predate the write that request is about.
      pendingRef.current = false;
      try {
        adoptRef.current();
        // Without throwOnError a failed refetch still resolves, and the cache
        // then still holds the pre-assistant graph we would reseed from.
        await client.invalidateQueries({ queryKey }, { throwOnError: true });

        // Navigation can land while the refetch is in flight. The canvas now
        // belongs to another process, and this graph is not its graph.
        if (processIdRef.current !== id) {
          cancelAdoptRef.current();
          return 'unchanged';
        }

        // The user can start editing while the refetch is in flight; reseeding
        // over that would discard what they just typed.
        if (!discardLocalEdits && isDirtyRef.current) {
          cancelAdoptRef.current();
          pendingRef.current = true;
          return 'deferred-dirty';
        }

        const fresh = client.getQueryData<ProcessDetailDto>(queryKey);
        if (!fresh || fresh.updatedAt === staleUpdatedAt) {
          cancelAdoptRef.current();
          setWaiting(false);
          return 'unchanged';
        }
        setGraphRef.current({ nodes: fresh.nodes, edges: fresh.edges });
        setWaiting(false);
        return 'applied';
      } catch {
        cancelAdoptRef.current();
        pendingRef.current = true;
        return 'failed';
      }
    },
    [setWaiting],
  );

  /**
   * Run attempts until one answers every request made so far, holding
   * {@code inFlightRef} for the whole sequence so nothing overlaps.
   *
   * The pass cap only bounds a pathological request rate; whatever is left
   * unanswered stays in {@code pendingRef} for the next trigger.
   */
  const applyFromServer = useCallback(
    (
      id: string,
      discardLocalEdits: boolean,
      after: Promise<unknown> | null,
    ): Promise<SyncOutcome> => {
      const runPasses = async (): Promise<SyncOutcome> => {
        try {
          let outcome: SyncOutcome = 'unchanged';
          let applied = false;
          for (let pass = 0; pass < MAX_COALESCED_PASSES; pass += 1) {
            if (processIdRef.current !== id) break;
            const served = requestSeqRef.current;
            outcome = await runAttempt(id, discardLocalEdits);
            applied = applied || outcome === 'applied';
            // These already owe themselves a retry.
            if (outcome === 'failed' || outcome === 'deferred-dirty') break;
            if (requestSeqRef.current === served) break;
            // A newer request arrived mid-attempt. Owe it now, so hitting the
            // pass cap leaves it for the next trigger instead of losing it.
            pendingRef.current = true;
          }
          // A later pass finding nothing new does not undo an earlier one that
          // put a new graph on the canvas.
          return applied ? 'applied' : outcome;
        } finally {
          if (mountedRef.current) setIsSyncing(false);
        }
      };

      setIsSyncing(true);
      // The slot below is claimed synchronously while the work itself waits for
      // `after` (or for one microtask). An attempt runs synchronously up to its
      // first `await`, and a queued attempt must not leave the slot observably
      // empty while it waits — either gap would let a second sequence start.
      const sequence = Promise.resolve(after)
        .catch(() => undefined)
        .then(runPasses);
      // Always release the slot, even on an unexpected rejection: a stuck
      // `inFlightRef` would block every later sync for the page's lifetime.
      const release = () => {
        if (inFlightRef.current === sequence) inFlightRef.current = null;
      };
      inFlightRef.current = sequence;
      void sequence.then(release, release);
      return sequence;
    },
    [runAttempt],
  );

  const syncNow = useCallback(async (): Promise<SyncOutcome> => {
    const id = processIdRef.current;
    if (!id) return 'unchanged';

    // Coalesce into the attempt already running rather than issuing a second
    // invalidate that would race it. Recorded as owed and as a newer request,
    // so the running sequence takes another pass for it.
    requestSeqRef.current += 1;
    if (inFlightRef.current) {
      pendingRef.current = true;
      return 'unchanged';
    }

    if (isDirtyRef.current) {
      pendingRef.current = true;
      await probeForNewVersion(id);
      return 'deferred-dirty';
    }

    return applyFromServer(id, false, null);
  }, [applyFromServer, probeForNewVersion]);

  const discardLocalEditsAndSync = useCallback((): Promise<SyncOutcome> => {
    const id = processIdRef.current;
    if (!id) return Promise.resolve('unchanged');
    // Queue rather than coalesce: this is an explicit user action, so it has
    // to actually happen, but it must not run alongside another attempt.
    return applyFromServer(id, true, inFlightRef.current);
  }, [applyFromServer]);

  // Drain an owed sync the moment the dirty guard lifts. A failed attempt
  // re-arms `pendingRef` without changing this effect's deps, so it waits for
  // the next real trigger instead of spinning.
  useEffect(() => {
    if (isDirty || !pendingRef.current) return;
    void syncNow();
  }, [isDirty, syncNow]);

  // Nothing is owed on a process we just navigated to.
  useEffect(() => {
    pendingRef.current = false;
    setDeferredChangesWaiting(false);
  }, [processId]);

  return {
    syncNow,
    discardLocalEditsAndSync,
    isSyncing,
    deferredChangesWaiting,
  };
};
